import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();

    if (honeypot) {
      setFormStatus('success');
      setEmail('');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'website_alt') {
        payload.append(key, value as string);
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d933hdk41f1vs39a6bu0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = serverMsg?.toLowerCase().includes('spam') || serverMsg?.toLowerCase().includes('form data is spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus('success');
        setFormError('');
        setEmail('');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  }, []);

  const quickLinks = [
    { label: t('navHome'), href: '/' },
    { label: t('navWanghong'), href: '/category/wanghong-fancha' },
    { label: t('navMingxing'), href: '/category/mingxing-tafang' },
    { label: t('navDasai'), href: '/category/meiri-dasai' },
    { label: t('navDujia'), href: '/category/dujia-shoufa' },
    { label: t('navAbout'), href: '/about' },
  ];

  const socialLinks = [
    { label: '微信公众号', href: '#' },
    { label: '新浪微博', href: '#' },
    { label: '抖音号', href: '#' },
  ];

  return (
    <footer className="bg-background-50 border-t border-background-200/70">
      <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          <div className="space-y-4">
            <h3 className="text-xl font-bold font-heading text-primary-500">{t('footerBrand')}</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{t('footerDesc1')}</p>
            <p className="text-sm text-foreground-600 leading-relaxed">{t('footerDesc2')}</p>
            <p className="text-sm text-foreground-600 leading-relaxed">{t('footerDesc3')}</p>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground-700 uppercase tracking-wider">{t('footerNewsletter')}</h4>
            <form
              data-readdy-form="d933hdk41f1vs39a6bu0"
              action="https://readdy.ai/api/form/d933hdk41f1vs39a6bu0"
              method="POST"
              onSubmit={handleSubmit}
              className="space-y-3"
            >
              <div className="flex items-center border-b border-foreground-600/30">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footerNewsletterPlaceholder')}
                  required
                  className="flex-1 bg-transparent py-2 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
                />
                <button type="submit" className="w-10 h-10 flex items-center justify-center text-primary-500 hover:text-primary-400 transition-colors">
                  <i className="ri-arrow-right-line text-xl" />
                </button>
              </div>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              {formStatus === 'success' && (
                <p className="text-sm text-primary-500">订阅成功！感谢您的关注</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-accent-500">{formError}</p>
              )}
              <p className="text-xs text-foreground-500">{t('footerNewsletterTip')}</p>
            </form>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground-700 uppercase tracking-wider">{t('footerLinks')}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground-700 uppercase tracking-wider">{t('footerSocial')}</h4>
            <ul className="space-y-2">
              {socialLinks.map((link, idx) => (
                <li key={idx}>
                  <a href={link.href} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors" rel="nofollow">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 lg:px-12 pb-8">
        <div className="text-center">
          <p className="text-[4rem] md:text-[7rem] lg:text-[9rem] font-black font-heading text-primary-500/10 leading-none tracking-tighter select-none">
            HEILIAO
          </p>
        </div>
        <div className="mt-6 pt-6 border-t border-background-200/70 text-center">
          <p className="text-xs text-foreground-500">{t('footerCopyright')}</p>
        </div>
      </div>
    </footer>
  );
}